import React from 'react';
import { Drawer, Descriptions, Tag, Button, Space, Typography, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useAuthStore from '../store/authStore';
import { WARNING_COLOR, WARNING_BG, daysUntilBirthday, pluralDays } from '../utils/birthday';

const { Text } = Typography;

interface Employee {
  id: number;
  tab_number?: string;
  last_name: string;
  first_name: string;
  middle_name?: string;
  phone?: string;
  birth_date?: string;
  hire_date?: string;
  gender?: string;
  is_official?: boolean;
  organization?: string;
  uniform_height?: string;
  uniform_size?: string;
  marital_status?: string;
  has_children?: boolean;
  snils?: string;
  status?: string;
  status_since?: string;
  position?: { id: number; name: string };
  department?: { id: number; name: string };
  workplace?: { id: number; name: string };
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  active:    { label: 'Работает',        color: 'green'   },
  on_leave:  { label: 'В отпуске',       color: 'orange'  },
  sick:      { label: 'На больничном',   color: 'gold'    },
  dismissed: { label: 'Уволен',          color: 'red'     },
};

interface Props {
  employee: Employee | null;
  open: boolean;
  onClose: () => void;
}

const EmployeeDrawer: React.FC<Props> = ({ employee, open, onClose }) => {
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canEdit = hasPermission('employees:write');

  if (!employee) return null;

  const fio = [employee.last_name, employee.first_name, employee.middle_name]
    .filter(Boolean).join(' ');

  const statusInfo = employee.status ? STATUS_LABELS[employee.status] : null;
  const hasUniform = employee.uniform_height || employee.uniform_size;

  return (
    <Drawer
      title={fio}
      open={open}
      onClose={onClose}
      width={460}
      extra={
        canEdit ? (
          <Button
            type="primary"
            onClick={() => { onClose(); navigate(`/employees/${employee.id}/edit`); }}
          >
            Редактировать
          </Button>
        ) : null
      }
    >
      {/* Работа */}
      <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
        Работа
      </Text>
      <Descriptions column={1} size="small" bordered style={{ marginTop: 6, marginBottom: 16 }}>
        {employee.tab_number && (
          <Descriptions.Item label="Таб. номер">{employee.tab_number}</Descriptions.Item>
        )}
        {employee.organization && (
          <Descriptions.Item label="Организация">
            <Tag color={employee.organization === 'ООО АвтоРегиум' ? 'blue' : 'green'}>
              {employee.organization}
            </Tag>
          </Descriptions.Item>
        )}
        {employee.department && (
          <Descriptions.Item label="Подразделение">{employee.department.name}</Descriptions.Item>
        )}
        {employee.position && (
          <Descriptions.Item label="Должность">{employee.position.name}</Descriptions.Item>
        )}
        {employee.workplace && (
          <Descriptions.Item label="Рабочее место">{employee.workplace.name}</Descriptions.Item>
        )}
        {employee.hire_date && (
          <Descriptions.Item label="Дата приёма">
            {dayjs(employee.hire_date).format('DD.MM.YYYY')}
          </Descriptions.Item>
        )}
        {'is_official' in employee && (
          <Descriptions.Item label="Официально">
            {employee.is_official ? <Tag color="green">Да</Tag> : <Tag color="orange">Нет</Tag>}
          </Descriptions.Item>
        )}
        {employee.status && statusInfo && (
          <Descriptions.Item label="Статус">
            <Space>
              <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
              {employee.status_since && (
                <span style={{ fontSize: 12, color: '#888' }}>
                  с {dayjs(employee.status_since).format('DD.MM.YYYY')}
                </span>
              )}
            </Space>
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Личные данные */}
      <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
        Личные данные
      </Text>
      <Descriptions column={1} size="small" bordered style={{ marginTop: 6, marginBottom: 16 }}>
        {employee.phone && (
          <Descriptions.Item label="Телефон">{employee.phone}</Descriptions.Item>
        )}
        {employee.birth_date && (() => {
          const days = daysUntilBirthday(employee.birth_date!);
          const isSoon = days <= 7;
          const highlightStyle = isSoon
            ? { background: WARNING_BG, borderColor: WARNING_COLOR }
            : undefined;
          return (
            <Descriptions.Item
              label="Дата рождения"
              labelStyle={highlightStyle}
              contentStyle={highlightStyle}
            >
              {dayjs(employee.birth_date).format('DD.MM.YYYY')}
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                ({dayjs().diff(dayjs(employee.birth_date), 'year')} лет)
              </Text>
              {isSoon && (
                <Tag
                  style={{
                    marginLeft: 8,
                    background: WARNING_COLOR,
                    borderColor: WARNING_COLOR,
                    color: '#fff',
                    fontWeight: 600,
                  }}
                >
                  {days === 0 ? '🎂 Сегодня!' : `через ${days} ${pluralDays(days)}`}
                </Tag>
              )}
            </Descriptions.Item>
          );
        })()}
        {employee.gender && (
          <Descriptions.Item label="Пол">{employee.gender}</Descriptions.Item>
        )}
        {employee.snils && (
          <Descriptions.Item label="СНИЛС">{employee.snils}</Descriptions.Item>
        )}
        <Descriptions.Item label="Семейное положение">
          {employee.marital_status === 'married' ? 'Женат / Замужем' : 'Не состоит в браке'}
        </Descriptions.Item>
        <Descriptions.Item label="Дети">
          {employee.has_children ? 'Есть' : 'Нет'}
        </Descriptions.Item>
      </Descriptions>

      {/* Размер одежды — только если заполнено */}
      {hasUniform && (
        <>
          <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
            Форма
          </Text>
          <Descriptions column={1} size="small" bordered style={{ marginTop: 6 }}>
            {employee.uniform_height && (
              <Descriptions.Item label="Рост">{employee.uniform_height}</Descriptions.Item>
            )}
            {employee.uniform_size && (
              <Descriptions.Item label="Размер">{employee.uniform_size}</Descriptions.Item>
            )}
          </Descriptions>
        </>
      )}
    </Drawer>
  );
};

export default EmployeeDrawer;
